/**
 * Front-my-End — Console View UI
 *
 * Manages the console logging panel, styling individual log levels,
 * generating formatted timestamps, reverse-chronological ordering (newest on top),
 * header filter buttons/counts, clipboard export, and deduplication.
 */
import { Modal } from '../core/modal.js';

export class ConsoleView {
  static MAX_ROWS = 500;

  constructor(outputEl, toolbarEl = null) {
    this.outputEl = outputEl;
    this.toolbarEl =
      toolbarEl ||
      (outputEl
        ? outputEl.parentElement?.querySelector('.console-toolbar') ||
          document.getElementById('console-toolbar')
        : null);

    this.rows = outputEl ? Array.from(outputEl.querySelectorAll('.console-row')) : [];
    this.rows.forEach((row) => {
      if (!row.dataset.level) {
        row.dataset.level = row.classList.contains('system-msg') ? 'system' : 'info';
      }
    });

    this.lastSignature = null;
    this.lastRow = this.rows[0] || null;
    this.lastCount = 1;
    this.filter = 'all';
    this.isPaused = this.rows.length >= ConsoleView.MAX_ROWS;
    this.hasShownPauseModal = false;

    this.initToolbar();
    this.updateFilterCounts();
    this.updateFilterIndicator();
  }

  static timestamp() {
    const date = new Date();
    return [
      String(date.getHours()).padStart(2, '0'),
      String(date.getMinutes()).padStart(2, '0'),
      String(date.getSeconds()).padStart(2, '0')
    ].join(':');
  }

  static normalizeLevel(level) {
    if (level === 'log') return 'info';
    if (['info', 'warn', 'error'].includes(level)) return level;
    if (level === 'system-msg' || level === 'system') return 'system';
    return 'info';
  }

  static sourceFor(level, message, details = {}) {
    if (details.source) return details.source;
    if (level === 'system') return 'System';
    if (message.startsWith('[Interaction]')) return 'Interaction';
    if (message.startsWith('[Navigation')) return 'Navigation';
    if (message.startsWith('[Security')) return 'Security';
    return 'Preview';
  }

  static textFrom(message) {
    if (message === null) return 'null';
    if (message === undefined) return 'undefined';
    if (typeof message === 'string') return message;
    if (typeof message === 'object') {
      if (message.message || message.text) return message.message || message.text;
      try {
        return JSON.stringify(message, null, 2);
      } catch (e) {
        return Object.prototype.toString.call(message);
      }
    }
    return String(message);
  }

  initToolbar() {
    if (!this.toolbarEl) return;

    const filterButtons = this.toolbarEl.querySelectorAll('.console-filter-btn');
    filterButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const targetFilter = btn.dataset.filter;
        if (targetFilter) {
          this.setFilter(targetFilter);
        }
      });
    });

    const copyBtn = this.toolbarEl.querySelector('#console-copy-btn');
    if (copyBtn) {
      copyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.copyAllLogs();
      });
    }

    const clearBtn = this.toolbarEl.querySelector('#console-clear-btn');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.clear();
      });
    }

    const resetBtn = this.toolbarEl.querySelector('#console-reset-filter-btn');
    if (resetBtn) {
      resetBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.setFilter('all');
      });
    }
  }

  setFilter(filter) {
    this.filter = filter || 'all';
    this.updateFilterButtons();
    this.updateFilterIndicator();
    this.renderRows();
  }

  updateFilterIndicator() {
    if (!this.toolbarEl) return;
    const indicator = this.toolbarEl.querySelector('#console-filter-indicator');
    const label = this.toolbarEl.querySelector('#console-active-filter-label');
    if (!indicator) return;
    if (this.filter === 'all') {
      indicator.hidden = true;
    } else {
      indicator.hidden = false;
      if (label) {
        const names = { info: 'Logs', warn: 'Warnings', error: 'Errors', system: 'System' };
        label.textContent = names[this.filter] || this.filter;
      }
    }
  }

  updateFilterButtons() {
    if (!this.toolbarEl) return;
    const buttons = this.toolbarEl.querySelectorAll('.console-filter-btn');
    buttons.forEach((btn) => {
      const isActive = btn.dataset.filter === this.filter;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
    });
  }

  getCounts() {
    const counts = { all: 0, info: 0, warn: 0, error: 0, system: 0 };
    for (const row of this.rows) {
      const lvl = row.dataset.level || 'info';
      const repeatEl = row.querySelector('.console-repeat');
      const count = repeatEl ? parseInt(repeatEl.dataset.count || '1', 10) || 1 : 1;
      counts.all += count;
      if (counts[lvl] !== undefined) {
        counts[lvl] += count;
      }
    }
    return counts;
  }

  updateFilterCounts() {
    if (!this.toolbarEl) return;
    const counts = this.getCounts();
    const buttons = this.toolbarEl.querySelectorAll('.console-filter-btn');
    buttons.forEach((btn) => {
      const filter = btn.dataset.filter;
      const count = counts[filter] ?? 0;
      let badge = btn.querySelector('.console-filter-count');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'console-filter-count';
        btn.appendChild(badge);
      }
      badge.textContent = String(count);
      badge.dataset.count = String(count);
    });
  }

  rowMatchesFilter(row) {
    const rowLevel = row.dataset.level || 'info';
    return this.filter === 'all' || rowLevel === this.filter;
  }

  renderRows() {
    if (!this.outputEl) return;
    this.outputEl.replaceChildren(...this.rows.filter((row) => this.rowMatchesFilter(row)));
    this.scrollToTop();
  }

  applyFilter() {
    this.renderRows();
  }

  scrollToTop() {
    if (!this.outputEl) return;
    this.outputEl.scrollTop = 0;
  }

  scrollToBottom() {
    if (!this.outputEl) return;
    this.outputEl.scrollTop = this.outputEl.scrollHeight;
  }

  renderTree(value, key = 'value') {
    if (value === null || typeof value !== 'object') {
      const leaf = document.createElement('span');
      leaf.className = 'console-tree-leaf';
      leaf.textContent = `${key}: ${String(value)}`;
      return leaf;
    }

    const details = document.createElement('details');
    details.className = 'console-tree';
    const summary = document.createElement('summary');
    const count = Array.isArray(value) ? value.length : Object.keys(value).length;
    summary.textContent = `${key} ${Array.isArray(value) ? 'Array' : 'Object'}(${count})`;
    details.appendChild(summary);

    const children = document.createElement('div');
    children.className = 'console-tree-children';
    Object.entries(value).forEach(([childKey, childValue]) => {
      children.appendChild(this.renderTree(childValue, childKey));
    });
    details.appendChild(children);
    return details;
  }

  renderEntryTrees(details) {
    const entries = Array.isArray(details.entries) ? details.entries : [];
    const treeEntries = entries.filter((entry) => entry && (entry.type === 'object' || entry.type === 'array'));
    if (!treeEntries.length) return null;

    const wrapper = document.createElement('div');
    wrapper.className = 'console-tree-list';
    treeEntries.forEach((entry, index) => {
      try {
        wrapper.appendChild(this.renderTree(JSON.parse(entry.preview), entry.type === 'array' ? `array ${index + 1}` : `object ${index + 1}`));
      } catch (_err) {
        const fallback = document.createElement('pre');
        fallback.className = 'console-details';
        fallback.textContent = entry.preview;
        wrapper.appendChild(fallback);
      }
    });
    return wrapper;
  }

  showPauseModal() {
    if (this.hasShownPauseModal) return;
    this.hasShownPauseModal = true;
    try {
      Modal.showMessage(
        'Console paused',
        `Console output reached ${ConsoleView.MAX_ROWS} lines. New logs are paused until you clear the console.`
      );
    } catch (err) {
      console.warn('[Front-my-End] Console output paused; clear the console to resume logs.', err);
    }
  }

  pauseIfFull() {
    if (this.rows.length < ConsoleView.MAX_ROWS) return false;
    this.isPaused = true;
    this.showPauseModal();
    return true;
  }

  append(level, message, details = {}) {
    if (!this.outputEl) return;

    if (this.isPaused || this.pauseIfFull()) {
      this.showPauseModal();
      return;
    }

    const normalized = ConsoleView.normalizeLevel(level);
    const bodyText = ConsoleView.textFrom(message);
    const source = ConsoleView.sourceFor(normalized, bodyText, details);
    const location = details.line
      ? `line ${details.line}${details.column ? `:${details.column}` : ''}`
      : '';
    const signature = `${normalized}|${source}|${location}|${bodyText}|${details.stack || ''}`;

    if (signature === this.lastSignature && this.lastRow) {
      this.lastCount += 1;
      const repeat = this.lastRow.querySelector('.console-repeat');
      if (repeat) {
        repeat.textContent = `x${this.lastCount}`;
        repeat.dataset.count = String(this.lastCount);
      }
      const timeSpan = this.lastRow.querySelector('.console-time');
      if (timeSpan) {
        timeSpan.textContent = ConsoleView.timestamp();
      }
      this.updateFilterCounts();
      if (this.lastRow.isConnected && this.rowMatchesFilter(this.lastRow)) {
        this.scrollToTop();
      }
      return;
    }

    const row = document.createElement('div');
    row.className = 'console-row';
    row.classList.add(normalized === 'system' ? 'system-msg' : `log-${normalized}`);
    row.dataset.level = normalized;

    const metaSpan = document.createElement('span');
    metaSpan.className = 'console-meta';

    const timeSpan = document.createElement('span');
    timeSpan.className = 'console-time';
    timeSpan.textContent = ConsoleView.timestamp();

    const levelSpan = document.createElement('span');
    levelSpan.className = `console-level console-level-${normalized}`;
    levelSpan.textContent = normalized === 'system' ? 'SYSTEM' : normalized.toUpperCase();

    const sourceSpan = document.createElement('span');
    sourceSpan.className = 'console-source';
    sourceSpan.textContent = source;

    const repeatSpan = document.createElement('span');
    repeatSpan.className = 'console-repeat';
    repeatSpan.textContent = 'x1';
    repeatSpan.dataset.count = '1';

    metaSpan.appendChild(timeSpan);
    metaSpan.appendChild(levelSpan);
    metaSpan.appendChild(sourceSpan);
    if (location) {
      const locationSpan = document.createElement('span');
      locationSpan.className = 'console-location';
      locationSpan.textContent = location;
      metaSpan.appendChild(locationSpan);
    }
    metaSpan.appendChild(repeatSpan);

    const bodySpan = document.createElement('span');
    bodySpan.className = 'console-body';
    bodySpan.textContent = bodyText;

    row.appendChild(metaSpan);
    row.appendChild(bodySpan);

    const tree = this.renderEntryTrees(details);
    if (tree) {
      row.appendChild(tree);
    }

    if (details.stack && details.stack !== bodyText) {
      const stackEl = document.createElement('pre');
      stackEl.className = 'console-details';
      stackEl.textContent = details.stack;
      row.appendChild(stackEl);
    }

    // Reverse-chronological: newest entry at the beginning
    this.rows.unshift(row);
    this.lastSignature = signature;
    this.lastRow = row;
    this.lastCount = 1;
    this.pauseIfFull();
    this.updateFilterCounts();

    // Prepend to display if matches current filter
    if (this.rowMatchesFilter(row)) {
      this.outputEl.prepend(row);
      this.scrollToTop();
    }
  }

  system(message) {
    this.append('system-msg', message);
  }

  clear() {
    if (!this.outputEl) return;
    this.rows = [];
    this.outputEl.replaceChildren();
    this.lastSignature = null;
    this.lastRow = null;
    this.lastCount = 1;
    this.isPaused = false;
    this.hasShownPauseModal = false;
    this.system('Console cleared.');
    this.updateFilterCounts();
  }

  formatRowText(row) {
    const time = row.querySelector('.console-time')?.textContent || '';
    const level = (row.dataset.level || 'info').toUpperCase();
    const source = row.querySelector('.console-source')?.textContent || 'System';
    const repeatEl = row.querySelector('.console-repeat');
    const repeatCount =
      repeatEl && repeatEl.dataset.count && repeatEl.dataset.count !== '1'
        ? ` (x${repeatEl.dataset.count})`
        : '';
    const locationEl = row.querySelector('.console-location');
    const location = locationEl?.textContent ? ` [${locationEl.textContent}]` : '';
    const body = row.querySelector('.console-body')?.textContent || '';

    let line = `[${time}] [${level}] [${source}]${location}${repeatCount} ${body}`;
    const details = row.querySelector('.console-details');
    if (details?.textContent) {
      line += `\n  ${details.textContent.replace(/\n/g, '\n  ')}`;
    }
    return line;
  }

  getFormattedLogsText(onlyFiltered = false) {
    const rowsToFormat = onlyFiltered
      ? this.rows.filter((r) => this.rowMatchesFilter(r))
      : this.rows;

    return rowsToFormat.map((row) => this.formatRowText(row)).join('\n');
  }

  async copyAllLogs() {
    const text = this.getFormattedLogsText();
    const btn = this.toolbarEl?.querySelector('#console-copy-btn');

    let copied = false;
    try {
      if (
        typeof navigator !== 'undefined' &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function' &&
        window.isSecureContext
      ) {
        await navigator.clipboard.writeText(text);
        copied = true;
      } else {
        copied = this.fallbackCopy(text);
      }
    } catch (_err) {
      copied = this.fallbackCopy(text);
    }

    if (btn && copied) {
      btn.classList.add('copied');
      const iconSpan = document.createElement('span');
      iconSpan.className = 'icon';
      iconSpan.textContent = '✓';
      btn.replaceChildren(iconSpan, document.createTextNode(' Copied!'));
      setTimeout(() => {
        btn.classList.remove('copied');
        const defaultIcon = document.createElement('span');
        defaultIcon.className = 'icon';
        defaultIcon.textContent = '📋';
        btn.replaceChildren(defaultIcon, document.createTextNode(' Copy'));
      }, 1500);
    }
    return copied;
  }

  fallbackCopy(text) {
    if (typeof document === 'undefined') return false;
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.top = '-9999px';
      textarea.style.left = '-9999px';
      textarea.setAttribute('readonly', '');
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      return success;
    } catch (_e) {
      return false;
    }
  }
}
